import React, { useState } from "react";
import pageStyles from "../styles/PageLayout.module.css";
import panelStyles from "../styles/Panel.module.css";
import styles from "../styles/SubscriptionsPage.module.css";
import PageHeader from "../shared/PageHeader";
import PageContent from "../shared/PageContent";
import { formatDateTime } from "../utils/formatters";

const SOURCE_OPTIONS = [
  { value: "all", label: "Все источники" },
  { value: "tochka", label: "Tochka" },
  { value: "unitpay", label: "Unitpay" },
  { value: "vkdonut", label: "VK Donut" },
  { value: "cloudpayments", label: "CloudPayments" }
];

function formatCountdown(daysLeft) {
  const days = Number(daysLeft || 0);
  if (!Number.isFinite(days)) return "—";
  if (days <= 0) return "сегодня";
  if (days === 1) return "1 день";
  if (days < 5) return `${days} дня`;
  return `${days} дней`;
}

function getSourceTone(source) {
  switch (source) {
    case "tochka":
      return styles.sourceTochka;
    case "unitpay":
      return styles.sourceUnitpay;
    case "vkdonut":
      return styles.sourceVkdonut;
    case "cloudpayments":
      return styles.sourceCloudpayments;
    default:
      return styles.sourceUnknown;
  }
}

const HUB_SOURCE_NAMES = {
  vkdonut: "VK Donut",
  tochka: "Точка",
  cloudpayments: "CloudPayments",
  promo: "Промо",
  gift: "Подарочная",
  referral_reward: "Реферальная",
  unknown: "Неизвестно"
};

const HUB_TARIFF_NAMES = {
  "1": "Lite",
  "2": "Pro",
  "3": "Max",
  "15": "Lite неделя",
  "16": "Pro неделя",
  "17": "Max неделя"
};

function HubMetricCard({ title, value, lines, hint }) {
  return (
    <div className={styles.hubCard}>
      <span className={styles.hubCardTitle}>{title}</span>
      <strong className={styles.hubCardValue}>{value}</strong>
      {Array.isArray(lines) && lines.length > 0 ? (
        <div className={styles.hubCardLines}>
          {lines.map((line, index) => (
            <span key={`${index}-${line}`}>{line}</span>
          ))}
        </div>
      ) : null}
      {hint ? <span className={styles.hubCardHint}>{hint}</span> : null}
    </div>
  );
}

function HubMetrics({ hub }) {
  if (!hub) {
    return (
      <section className={styles.hubGrid}>
        <HubMetricCard
          title="Сводка Payment Core"
          value="—"
          hint="Данные хаба недоступны"
        />
      </section>
    );
  }

  const autorenew = hub.autorenew || {};
  const premiumMonthly = autorenew.premiumMonthly || {};
  const premiumWeekly = autorenew.premiumWeekly || {};
  const packages = autorenew.packages || {};
  const packagesByProduct = packages.byProduct || {};
  const oneTime = hub.oneTime || {};
  const premiumUsers = hub.premiumUsers || {};

  const premiumMonthlyActive = Number(premiumMonthly.active || 0);
  const premiumWeeklyActive = Number(premiumWeekly.active || 0);
  const packagesActive = Number(packages.active || 0);
  // Премиум (месяц + неделя) + пакеты кредитов.
  const activeAutorenewTotal =
    premiumMonthlyActive + premiumWeeklyActive + packagesActive || Number(autorenew.activeTotal || 0);

  const packageLines = Object.entries(packagesByProduct)
    .filter(([, item]) => Number(item?.active || 0) > 0)
    .map(([, item]) => `${item.name} — ${Number(item.active || 0)}`);

  const activeAutorenewLines = [
    `Премиум месяц — ${premiumMonthlyActive}`,
    `Премиум неделя — ${premiumWeeklyActive}`,
    `Пакеты кредитов — ${packagesActive}`
  ];

  const premiumUsersTotal = Number(premiumUsers.total || 0);
  const premiumWithHub = Number(
    premiumUsers.withHubAutorenew != null
      ? premiumUsers.withHubAutorenew
      : premiumMonthlyActive + premiumWeeklyActive
  );
  const premiumWithoutHub = Math.max(0, premiumUsersTotal - premiumWithHub);

  const oneTimeLines = (Array.isArray(oneTime.byProduct) ? oneTime.byProduct : [])
    .map((item) => `${item.name || `Продукт #${item.id}`} — ${Number(item.count || 0)}`);

  const sourceEntries = Object.entries(premiumUsers.bySource || {});
  const sourceTotal = sourceEntries.reduce((sum, [, count]) => sum + Number(count || 0), 0);
  const sourceLines = sourceEntries
    .map(([source, count]) => `${HUB_SOURCE_NAMES[source] || source} — ${Number(count || 0)}`);

  const tariffEntries = Object.entries(premiumUsers.byTariff || {});
  const tariffTotal = tariffEntries.reduce((sum, [, count]) => sum + Number(count || 0), 0);
  const tariffLines = tariffEntries
    .map(([tariffId, count]) => `${HUB_TARIFF_NAMES[tariffId] || `#${tariffId}`} — ${Number(count || 0)}`);

  const premiumUsersLines = [
    `С автопродлением Payment Core — ${premiumWithHub}`,
    `Без автопродления (подарок / остаток срока / иное) — ${premiumWithoutHub}`
  ];

  return (
    <section className={styles.hubGrid}>
      <HubMetricCard
        title="Активных автопродлений"
        value={activeAutorenewTotal}
        lines={activeAutorenewLines}
        hint="Сумма: премиум месяц + премиум неделя + пакеты кредитов в Payment Core"
      />
      <HubMetricCard
        title="Премиум месяц (автопродления)"
        value={premiumMonthlyActive}
        hint={`Только автосписания Lite/Pro/Max месяц в Payment Core (продукты 1–3), на паузе: ${Number(premiumMonthly.paused || 0)}`}
      />
      <HubMetricCard
        title="Премиум неделя (автопродления)"
        value={premiumWeeklyActive}
        hint={`Только автосписания Lite/Pro/Max неделя в Payment Core (продукты 15–17), на паузе: ${Number(premiumWeekly.paused || 0)}`}
      />
      <HubMetricCard
        title="Пакеты кредитов (автопродления)"
        value={packagesActive}
        lines={packageLines}
        hint="Активные подписки на пакеты кредитов по видам (продукты 10, 12, 14, 19)"
      />
      <HubMetricCard
        title="Одноразовые покупки"
        value={Number(oneTime.total || 0)}
        lines={oneTimeLines}
        hint="Первичные (не автопродление) оплаты по всем продуктам за всё время"
      />
      <HubMetricCard
        title="Премиум-подписчиков (все источники)"
        value={premiumUsersTotal}
        lines={premiumUsersLines}
        hint="Все с активным премиумом в боте. Автопродления Payment Core (месяц+неделя) — отдельная метрика и может отличаться."
      />
      <HubMetricCard
        title="По источникам"
        value={sourceTotal || premiumUsersTotal || "—"}
        lines={sourceLines}
        hint="Те же премиум-подписчики бота, разбивка по последнему источнику оплаты"
      />
      <HubMetricCard
        title="По тарифам"
        value={tariffTotal || premiumUsersTotal || "—"}
        lines={tariffLines}
        hint={`Разбивка тех же ${tariffTotal || premiumUsersTotal} премиум-подписчиков бота по тарифу — не равно автопродлениям (${premiumMonthlyActive}+${premiumWeeklyActive}=${premiumMonthlyActive + premiumWeeklyActive}).`}
      />
      <HubMetricCard
        title="На паузе / отменено"
        value={`${Number(autorenew.paused || 0)} / ${Number(autorenew.cancelled || 0)}`}
        hint="Статусы автопродлений в Payment Core"
      />
    </section>
  );
}

export function SubscriptionsPage({ subscriptions }) {
  const { state, actions } = subscriptions;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const selectedSourceLabel =
    SOURCE_OPTIONS.find((item) => item.value === state.sourceFilter)?.label || "Все источники";

  return (
    <div className={pageStyles.page}>
      <PageHeader
        title="Подписки"
        description="Таблица активных подписок с названием тарифа из cost.json, сроком окончания и источником оплаты."
        sticky={false}
        actions={
          <>
            <input
              className={panelStyles.input}
              type="search"
              placeholder="Поиск по id, имени, тарифу или источнику"
              value={state.query}
              onChange={(event) => {
                actions.setQuery(event.target.value);
                actions.setOffset(0);
              }}
            />
            <button
              type="button"
              className={panelStyles.buttonSecondary}
              onClick={() => setFiltersOpen((current) => !current)}
            >
              {filtersOpen ? "Скрыть фильтры" : "Фильтры"}
            </button>
            <button type="button" className={panelStyles.button} onClick={actions.reload}>
              Обновить
            </button>
          </>
        }
      />

      <PageContent>
        <section className={styles.toolbarCard}>
          <div className={styles.toolbarTopRow}>
            <div className={styles.toolbarMeta}>
              <span>Сейчас показано</span>
              <strong>
                {state.items.length} из {state.counts.active}
              </strong>
            </div>
            <div className={styles.toolbarMeta}>
              <span>Фильтр источника</span>
              <strong>{selectedSourceLabel}</strong>
            </div>
            <div className={styles.toolbarMeta}>
              <span>Сортировка</span>
              <strong>{state.orderBy === "endsAt" ? "По сроку окончания" : state.orderBy}</strong>
            </div>
          </div>

          {filtersOpen ? (
            <div className={styles.toolbarFilters}>
              <label className={styles.filterField}>
                <span>Сортировка</span>
                <select
                  className={panelStyles.select}
                  value={state.orderBy}
                  onChange={(event) => actions.setOrderBy(event.target.value)}
                >
                  <option value="endsAt">Срок окончания</option>
                  <option value="id">ID</option>
                  <option value="name">Имя</option>
                  <option value="tariff">Тариф</option>
                  <option value="source">Источник</option>
                </select>
              </label>

              <label className={styles.filterField}>
                <span>Направление</span>
                <button type="button" className={panelStyles.buttonSecondary} onClick={actions.toggleOrderDir}>
                  {state.orderDir === "ASC" ? "По возрастанию" : "По убыванию"}
                </button>
              </label>

              <label className={styles.filterField}>
                <span>Источник оплаты</span>
                <select
                  className={panelStyles.select}
                  value={state.sourceFilter}
                  onChange={(event) => {
                    actions.setSourceFilter(event.target.value);
                    actions.setOffset(0);
                  }}
                >
                  {SOURCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.filterField}>
                <span>Строк на странице</span>
                <select
                  className={panelStyles.select}
                  value={state.limit}
                  onChange={(event) => {
                    actions.setLimit(Number(event.target.value));
                    actions.setOffset(0);
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </label>
            </div>
          ) : null}
        </section>

        <section className={styles.summaryBar}>
          <div className={styles.summaryCard}>
            <span>Активных подписок</span>
            <strong>{state.counts.active}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Заканчиваются скоро</span>
            <strong>{state.counts.expiringSoon}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Страница</span>
            <strong>
              {state.page} / {state.totalPages}
            </strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Фильтр</span>
            <strong>{selectedSourceLabel}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>На экране</span>
            <strong>{state.items.length}</strong>
          </div>
        </section>

        <section className={styles.sourceSummary}>
          {SOURCE_OPTIONS.slice(1).map((option) => (
            <div key={option.value} className={styles.sourceSummaryCard}>
              <span>{option.label}</span>
              <strong>{state.counts.bySource?.[option.value] || 0}</strong>
            </div>
          ))}
        </section>

        {state.error ? <div className={panelStyles.error}>{state.error}</div> : null}

        <HubMetrics hub={state.hub} />

        <section className={styles.tableCard}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Имя</th>
                  <th>Тариф</th>
                  <th>Закончится</th>
                  <th>Осталось</th>
                  <th>Оплата через</th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  <tr>
                    <td colSpan={6} className={styles.tableStateCell}>
                      Загружаем подписки...
                    </td>
                  </tr>
                ) : state.items.length > 0 ? (
                  state.items.map((item) => {
                    const isSoon = Number(item.daysLeft || 0) <= 7;
                    return (
                      <tr key={item.id} className={isSoon ? styles.rowSoon : ""}>
                        <td className={styles.mono}>{item.id}</td>
                        <td className={styles.nameCell}>{item.first_name || "—"}</td>
                        <td>
                          <div className={styles.tariffCell}>
                            <strong>{item.tariffName || "—"}</strong>
                            <span>#{item.tariffId || item.premiumId || "—"}</span>
                          </div>
                        </td>
                        <td>{formatDateTime(item.endsAtIso)}</td>
                        <td>{formatCountdown(item.daysLeft)}</td>
                        <td>
                          <span className={`${styles.sourceBadge} ${getSourceTone(item.source)}`}>
                            {item.source || "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.tableStateCell}>
                      Нет активных подписок для выбранного фильтра.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <button
              type="button"
              className={panelStyles.buttonSecondary}
              disabled={state.page <= 1 || state.loading}
              onClick={() => actions.setPage(state.page - 1)}
            >
              Назад
            </button>
            <div className={styles.pageIndicator}>
              {state.page} / {state.totalPages}
            </div>
            <button
              type="button"
              className={panelStyles.buttonSecondary}
              disabled={state.page >= state.totalPages || state.loading}
              onClick={() => actions.setPage(state.page + 1)}
            >
              Вперёд
            </button>
          </div>
        </section>
      </PageContent>
    </div>
  );
}

export default SubscriptionsPage;
